import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  extractLangFromPath,
  generateHreflangs,
  buildAlternateUrl,
  stripLangFromPath,
  DEFAULT_LANGUAGE,
} from '../lib/i18nRouting';

const HREFLANG_MARKER = 'data-dronemaps-hreflang';

/**
 * Hook : synchronise i18n avec la langue présente dans l'URL.
 */
export function useSyncLanguageFromURL() {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const urlLang = extractLangFromPath(location.pathname);
    // If the URL has no language prefix, we are on the default language (fr).
    // Otherwise the URL prefix wins and i18n must follow.
    const targetLang = urlLang || DEFAULT_LANGUAGE;
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [location.pathname, i18n]);
}

/**
 * Composant : injecte directement dans `document.head` les balises
 * `<link rel="alternate" hreflang>` + canonical pour la page courante.
 *
 * On utilise une manipulation DOM directe (pas Helmet) pour éviter les
 * problèmes de flush async de react-helmet-async lorsqu'on rend plusieurs
 * `<Helmet>` éparpillés dans la même page.
 */
export function HreflangTags() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const cleanPath = stripLangFromPath(location.pathname);
    const hreflangs = generateHreflangs(cleanPath);
    const currentLang = extractLangFromPath(location.pathname) || i18n.language || DEFAULT_LANGUAGE;
    const canonicalHref = buildAlternateUrl(cleanPath, currentLang);

    // Remove previously injected tags
    document
      .querySelectorAll(`link[${HREFLANG_MARKER}]`)
      .forEach((el) => el.remove());

    // Inject canonical
    const canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', canonicalHref);
    canonical.setAttribute(HREFLANG_MARKER, 'canonical');
    document.head.appendChild(canonical);

    // Inject hreflang alternates
    hreflangs.forEach(({ hreflang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      link.setAttribute(HREFLANG_MARKER, hreflang);
      document.head.appendChild(link);
    });

    return () => {
      document
        .querySelectorAll(`link[${HREFLANG_MARKER}]`)
        .forEach((el) => el.remove());
    };
  }, [location.pathname, i18n.language]);

  return null;
}
